import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckCircleFilled,
  InboxOutlined,
  ReloadOutlined,
  SearchOutlined,
  FolderOutlined,
  AppstoreOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Empty, Input, Modal, Pagination, Popconfirm, Select, Space, Spin, Tabs, Tag, Upload, message } from 'antd';
import { useTranslation } from 'react-i18next';
import galleryService, { GALLERY_CORE_FOLDER_SLUGS } from '../services/gallery';
import { resolveGalleryAssetUrl } from '../helpers/extraValueImg';
import { beforeUploadMaxSize } from '../helpers/uploadLimits';

const { TabPane } = Tabs;
const { Dragger } = Upload;
const DEFAULT_FOLDER = 'extras';
const ALL_FOLDER = '__all__';
const UNUSED_FOLDER = '__unused__';
/**
 * Page sizes the picker offers.
 *
 * 24 was hard-coded with the size changer switched off, so a shop with several
 * hundred assets paged through them two dozen at a time and could not do
 * anything about it. 96 fills a laptop screen; more than that and the grid
 * costs more to render than the paging saves.
 */
const PAGE_SIZES = [24, 48, 96];
const DEFAULT_PER_PAGE = PAGE_SIZES[0];

/** Folders that live in customer-generated storage; hidden from the
 *  admin picker so pickers never surface a customer's ID scan or
 *  profile pic when looking for a device photo. Also snapshot-protected
 *  folders that must never be deleted. */
const HIDDEN_FOLDERS = new Set([
  'users',
  'reviews',
  'receipts',
  'order_refunds',
]);

/**
 * Media library panel — the reusable core rendered inside both the
 * picker modal (`mode="picker"`) and the standalone `/media-library`
 * page (`mode="manager"`).
 *
 * In picker mode a tile click fires `onPick(image)`; in manager mode
 * every tile shows a delete-on-hover control and clicking a tile just
 * previews it in a lightbox.
 */
export function MediaLibraryPanel({
  mode = 'picker',
  onPick,
  /**
   * Pick several at once.
   *
   * Opt-in, because most callers fill a single slot — a brand logo, a category
   * image — and for them one tap being the whole interaction is right. A
   * variant with five angles is the case this exists for.
   */
  multiple = false,
  defaultFolder = DEFAULT_FOLDER,
  accept = '.png,.jpg,.jpeg,.webp,.svg,.jfif',
  /**
   * One line of advice from whoever opened the picker, shown on the upload
   * tab under the destination folder.
   *
   * The dialog knows where a file is going but not what it is for, so it
   * could not tell anybody that a popup background wants 1600x1200 and a
   * popup's top banner does not. The caller knows; this is how it says so.
   */
  hint,
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('library');
  const [folder, setFolder] = useState(ALL_FOLDER);
  const [uploadFolder, setUploadFolder] = useState(defaultFolder);
  const [availableFolders, setAvailableFolders] = useState(GALLERY_CORE_FOLDER_SLUGS);
  const [folderCounts, setFolderCounts] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  /*
   * The term actually sent to the API, a beat behind the input.
   *
   * The box used to filter `items` — the page already on screen — so searching
   * for a file that happened to live on page four of the library found nothing
   * and looked broken. It is a server query now, which means it has to be
   * debounced, and that the results are the whole library rather than a slice.
   */
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  /** How far a drop has got: { done, total } while it is going up, else null. */
  const [progress, setProgress] = useState(null);
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState(null); // { url, title }
  /** Chosen tiles, keyed by url so the same file cannot be added twice. */
  const [selected, setSelected] = useState([]);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const loadFolders = useCallback(() => {
    galleryService
      .getAllFolders({ includeFiles: false })
      .then((res) => {
        const folders = res?.folders ?? res?.data?.folders ?? [];
        const slugs = [];
        const counts = {};
        folders.forEach((f) => {
          const slug = String(f.slug ?? f.name ?? '').toLowerCase().replace(/-/g, '_');
          if (!slug) return;
          slugs.push(slug);
          counts[slug] = f.fileCount ?? 0;
        });
        if (slugs.length) setAvailableFolders(slugs);
        setFolderCounts(counts);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadFolders(); }, [loadFolders]);

  // 350ms: long enough that typing a filename is one request, short enough
  // that the grid feels like it is keeping up.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  /*
   * Anything that changes what the list contains sends us back to page one.
   *
   * Switching from a folder of 300 files to one of 12 while on page 5 asked the
   * API for a page that does not exist and drew an empty grid, which reads as
   * an empty folder.
   */
  useEffect(() => {
    setPage(1);
  }, [folder, perPage]);

  const loadFiles = useCallback(() => {
    setLoading(true);
    let call;
    if (folder === UNUSED_FOLDER) {
      call = galleryService.listUnused({ page, perPage });
    } else if (folder === ALL_FOLDER) {
      call = galleryService.paginateAll({ page, perPage, search: searchTerm || undefined });
    } else {
      call = galleryService.getAll({ type: folder, page, perPage, search: searchTerm || undefined });
    }
    call
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        const meta = res?.meta ?? res?.data?.meta ?? {};
        setItems(list);
        setTotal(meta.total ?? list.length);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [folder, page, perPage, searchTerm]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  /*
   * The library and the folders search on the server now, so what comes back is
   * already the answer and filtering it again would only hide matches that are
   * on other pages.
   *
   * The unused-file audit is the exception: it scans every model to work out
   * what nothing references, and has no search of its own, so a client-side
   * narrowing of the page in hand is the best that view can offer.
   */
  const filteredItems = useMemo(() => {
    if (folder !== UNUSED_FOLDER) return items;
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      String(i?.title ?? i?.path ?? i?.url ?? '').toLowerCase().includes(q),
    );
  }, [items, search, folder]);

  /**
   * Whether this tile is one of the chosen ones.
   *
   * A tile is identified by where its file lives, which is the right key:
   * two tiles of the same file are the same choice. The empty string is
   * the exception and has to be excluded by hand, because a file with no
   * address matches every other file with no address — tap one and the
   * whole grid lights up while the counter underneath says one, which is
   * not a selection bug the eye can make sense of.
   */
  const isSelected = (url) => Boolean(url) && selected.some((item) => item.url === url);

  const handleTileClick = (record) => {
    const path = record?.path || record?.url || '';
    const url = resolveGalleryAssetUrl(path);
    // Nothing to choose, and choosing it would mark every other addressless
    // tile as chosen too. See `isSelected`.
    if (!url) return;
    const name = record?.title || url.split('/').pop() || 'image';
    if (mode === 'picker' && multiple) {
      // Second tap on a chosen tile takes it back out — the only way to correct
      // a mis-tap without closing the whole picker.
      setSelected((prev) =>
        prev.some((item) => item.url === url)
          ? prev.filter((item) => item.url !== url)
          : [...prev, { url, path, name }],
      );
      return;
    }
    if (mode === 'picker' && onPick) {
      onPick({
        url,
        path,
        name: record?.title || url.split('/').pop() || 'image',
      });
    } else {
      setPreview({ url, title: record?.title });
    }
  };

  const deleteOne = async (record) => {
    try {
      await galleryService.deleteFile(record?.path || record?.url);
      message.success(t('successfully.deleted'));
      loadFiles();
      loadFolders();
    } catch (err) {
      message.error(err?.response?.data?.message ?? err?.message ?? t('error'));
    }
  };

  const deleteAllUnused = async () => {
    setLoading(true);
    try {
      const res = await galleryService.deleteAllUnused();
      const n = res?.data?.deleted ?? res?.deleted ?? 0;
      message.success(t('media.library.unused.deleted', { count: n }) || `${n} files deleted`);
      loadFiles();
      loadFolders();
    } catch (err) {
      message.error(err?.response?.data?.message ?? err?.message ?? t('error'));
    } finally {
      setLoading(false);
    }
  };

  /** Send one file and describe what came back the way `onPick` expects. */
  const uploadOne = async (file) => {
    const fd = new FormData();
    fd.append('folderName', uploadFolder || DEFAULT_FOLDER);
    fd.append('image', file);
    const res = await galleryService.uploadToGallery(fd);
    const record = Array.isArray(res?.data) ? res.data[0] : res?.data ?? res ?? {};
    const path = record?.path || record?.url || '';
    return {
      url: resolveGalleryAssetUrl(path),
      /*
        The raw storage path as well as the resolved URL.

        Callers want different things and both are right. The variant panel
        stores what the storefront will render, so it takes the URL; the product
        gallery stores what its own uploads store, which is the path — and a
        list where half the entries are absolute URLs and half are paths breaks
        the next time somebody moves the asset host.
      */
      path,
      name: record?.title || file.name,
      file,
    };
  };

  /**
   * Send a whole drop at once.
   *
   * This took one file. Dropping five on it uploaded the first and silently
   * ignored the other four, which for a variant with five angles is the whole
   * job done four more times by hand.
   *
   * Three at a time rather than all at once or strictly one after another. All
   * at once and ten 10MB files open ten sockets the browser queues anyway, with
   * nothing to say how far along it is; one after another is honest and three
   * times slower than it needs to be. Three keeps the connection busy and still
   * lets the count below mean something.
   *
   * A file that fails does not take the others with it. What arrived is handed
   * over and the failures are counted, because losing four good uploads to one
   * bad one is the worst of the available behaviours.
   */
  const uploadMany = async (files) => {
    setUploading(true);
    setProgress({ done: 0, total: files.length });
    const done = [];
    let failed = 0;
    let next = 0;
    const worker = async () => {
      for (;;) {
        const i = next;
        next += 1;
        if (i >= files.length) return;
        try {
          // eslint-disable-next-line no-await-in-loop
          done.push(await uploadOne(files[i]));
        } catch (err) {
          failed += 1;
          if (failed === 1) {
            message.error(err?.response?.data?.message ?? err?.message ?? t('error'));
          }
        }
        setProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
      }
    };
    try {
      await Promise.all([worker(), worker(), worker()]);
    } finally {
      setUploading(false);
      setProgress(null);
    }
    if (failed && done.length) {
      message.warning(
        t('media.library.upload.partial', {
          count: failed,
          defaultValue: failed + ' file(s) could not be uploaded',
        }),
      );
    }
    if (!done.length) return;
    if (mode === 'picker' && onPick) {
      /* A single-slot picker gets the first one: there is only one slot to put
         it in, and refusing the whole drop helps nobody. */
      onPick(multiple ? done : done[0]);
      return;
    }
    message.success(t('successfully.saved'));
    setTab('library');
    setFolder(uploadFolder || DEFAULT_FOLDER);
    setPage(1);
    loadFiles();
    loadFolders();
  };

  const visibleFolders = useMemo(
    () => availableFolders.filter(
      (s) => !HIDDEN_FOLDERS.has(s) && (folderCounts[s] ?? 0) > 0,
    ),
    [availableFolders, folderCounts],
  );

  const totalAll = useMemo(
    () => visibleFolders.reduce((n, s) => n + (folderCounts[s] || 0), 0),
    [visibleFolders, folderCounts],
  );

  const uploadFolderOptions = useMemo(
    () => availableFolders
      .filter((s) => !HIDDEN_FOLDERS.has(s))
      .map((s) => ({ label: labelize(s), value: s })),
    [availableFolders],
  );

  const isUnusedView = folder === UNUSED_FOLDER;
  const showTileDelete = mode === 'manager' || isUnusedView;

  return (
    <div className='ks-media'>
      <Tabs
        activeKey={tab}
        onChange={setTab}
        tabBarStyle={{ margin: 0, padding: '0 16px' }}
      >
        <TabPane
          key='library'
          tab={<span><AppstoreOutlined /> {t('media.library.browse')}</span>}
        >
          <div className='ks-media__body'>
            <aside className='ks-media__rail'>
              <button
                type='button'
                className={`ks-media__folder${folder === ALL_FOLDER ? ' is-active' : ''}`}
                onClick={() => { setFolder(ALL_FOLDER); setPage(1); }}
              >
                <AppstoreOutlined />
                <span className='ks-media__folder-name'>{t('media.library.all')}</span>
                <span className='ks-media__folder-count'>{totalAll || ''}</span>
              </button>
              {visibleFolders.map((slug) => {
                const cnt = folderCounts[slug] ?? 0;
                const active = folder === slug;
                return (
                  <button
                    key={slug}
                    type='button'
                    className={`ks-media__folder${active ? ' is-active' : ''}`}
                    onClick={() => { setFolder(slug); setPage(1); }}
                  >
                    <FolderOutlined />
                    <span className='ks-media__folder-name'>{labelize(slug)}</span>
                    <span className='ks-media__folder-count'>{cnt || ''}</span>
                  </button>
                );
              })}
              <div className='ks-media__rail-sep' />
              <button
                type='button'
                className={`ks-media__folder ks-media__folder--warn${folder === UNUSED_FOLDER ? ' is-active' : ''}`}
                onClick={() => { setFolder(UNUSED_FOLDER); setPage(1); }}
                title={t('media.library.unused.hint')}
              >
                <WarningOutlined />
                <span className='ks-media__folder-name'>{t('media.library.unused')}</span>
              </button>
            </aside>

            <section className='ks-media__pane'>
              <div className='ks-media__toolbar'>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder={t('search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: 320 }}
                />
                <div style={{ flex: 1 }} />
                <Button icon={<ReloadOutlined />} onClick={() => { loadFiles(); loadFolders(); }} loading={loading}>
                  {t('refresh')}
                </Button>
                {isUnusedView && items.length > 0 ? (
                  <Popconfirm
                    title={t('media.library.unused.delete.all.confirm', { count: total }) ||
                      `Delete all ${total} unused files?`}
                    okText={t('delete')}
                    okButtonProps={{ danger: true }}
                    cancelText={t('cancel')}
                    icon={<ExclamationCircleOutlined style={{ color: '#ef4444' }} />}
                    onConfirm={deleteAllUnused}
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      {t('media.library.unused.delete.all')}
                    </Button>
                  </Popconfirm>
                ) : (
                  <Button
                    type='primary'
                    icon={<CloudUploadOutlined />}
                    onClick={() => setTab('upload')}
                  >
                    {t('media.library.upload')}
                  </Button>
                )}
              </div>
              {isUnusedView ? (
                <div className='ks-media__notice'>
                  <ExclamationCircleOutlined />
                  <span>{t('media.library.unused.hint')}</span>
                  <Tag color='orange' style={{ marginLeft: 'auto' }}>
                    {total} {t('media.library.unused.count')}
                  </Tag>
                </div>
              ) : null}
              <div className='ks-media__grid-wrap'>
                <Spin spinning={loading}>
                  {filteredItems.length === 0 ? (
                    <Empty description={t('no.data')} style={{ padding: '60px 0' }} />
                  ) : (
                    <div className='ks-media__grid'>
                      {filteredItems.map((item) => {
                        const url = resolveGalleryAssetUrl(item?.url || item?.path);
                        const kind = String(item?.media_kind || item?.mime || '').toLowerCase();
                        const isImage = kind === 'image' || kind.startsWith('image/') || !kind;
                        return (
                          <button
                            key={item.id || url}
                            type='button'
                            className={`ks-media__tile${
                              isSelected(url) ? ' is-selected' : ''
                            }`}
                            onClick={() => handleTileClick(item)}
                            title={item?.title}
                          >
                            {isImage ? (
                              <img
                                src={url}
                                alt={item?.title}
                                onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
                              />
                            ) : (
                              <div className='ks-media__non-image'>
                                {String(item?.title || '').slice(0, 30)}
                              </div>
                            )}
                            <span className='ks-media__hover'>
                              <CheckCircleFilled />
                              <span className='ks-media__hover-label'>
                                {mode !== 'picker' ? t('preview') : isSelected(url) ? t('remove') : t('select')}
                              </span>
                            </span>
                            {item?.title ? (
                              <span className='ks-media__caption'>{item.title}</span>
                            ) : null}
                            {showTileDelete ? (
                              <Popconfirm
                                title={t('media.library.delete.confirm')}
                                okText={t('delete')}
                                okButtonProps={{ danger: true }}
                                cancelText={t('cancel')}
                                onConfirm={(e) => { e?.stopPropagation?.(); deleteOne(item); }}
                                onCancel={(e) => e?.stopPropagation?.()}
                              >
                                <span
                                  className='ks-media__tile-delete'
                                  onClick={(e) => e.stopPropagation()}
                                  title={t('delete')}
                                >
                                  <DeleteOutlined />
                                </span>
                              </Popconfirm>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </Spin>
              </div>
              {/*
                Always shown once there is more than one page's worth, and always
                with numbers. It used to collapse to a bare prev/next past 500
                files — exactly the size at which you need to jump rather than
                click forward twenty times — and the size changer was off, so 24
                per page was the only option there has ever been.
              */}
              {/* Multi-select needs a moment to say "these ones" — a tile tap
                  no longer closes the picker, so something has to. */}
              {mode === 'picker' && multiple ? (
                <div className='ks-media__confirm'>
                  <span className='ks-media__confirm-count'>
                    {t('media.library.selectedCount', {
                      count: selected.length,
                      defaultValue: `${selected.length} selected`,
                    })}
                  </span>
                  <Space>
                    <Button size='small' onClick={() => setSelected([])} disabled={!selected.length}>
                      {t('clear')}
                    </Button>
                    <Button
                      size='small'
                      type='primary'
                      disabled={!selected.length}
                      onClick={() => onPick?.(selected)}
                    >
                      {t('add')}
                    </Button>
                  </Space>
                </div>
              ) : null}
              {total > 0 ? (
                <div className='ks-media__pager'>
                  <Pagination
                    current={page}
                    pageSize={perPage}
                    total={total}
                    onChange={setPage}
                    pageSizeOptions={PAGE_SIZES}
                    showSizeChanger={total > PAGE_SIZES[0]}
                    onShowSizeChange={(_, size) => setPerPage(size)}
                    showQuickJumper={total > perPage * 5}
                    size='small'
                    // "1-24 of 312" rather than a row of numbers with no scale.
                    showTotal={(count, [from, to]) =>
                      t('media.library.pagerTotal', {
                        from,
                        to,
                        total: count,
                        defaultValue: `${from}-${to} of ${count}`,
                      })
                    }
                  />
                </div>
              ) : null}
            </section>
          </div>
        </TabPane>

        <TabPane
          key='upload'
          tab={<span><CloudUploadOutlined /> {t('media.library.upload')}</span>}
        >
          <div className='ks-media__upload'>
            {mode === 'manager' ? (
              <Space size={12} align='center' style={{ marginBottom: 16 }}>
                <span style={{ color: '#64748b' }}>{t('media.library.upload.to')}:</span>
                <Select
                  value={uploadFolder}
                  onChange={setUploadFolder}
                  style={{ minWidth: 180 }}
                  options={uploadFolderOptions}
                />
              </Space>
            ) : (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#64748b' }}>
                {t('media.library.upload.auto.to')}{' '}
                <b style={{ color: '#0f172a' }}>{labelize(uploadFolder || DEFAULT_FOLDER)}</b>
              </div>
            )}
            {hint ? (
              <div style={{ marginBottom: 12, fontSize: 13, color: '#64748b' }}>{hint}</div>
            ) : null}
            <Dragger
              accept={accept}
              multiple
              showUploadList={false}
              disabled={uploading}
              /*
                Called once per file, with the whole drop each time.

                So the batch is assembled on the first call and every later one
                is waved through — checking `batch[0] !== file` is how antd lets
                you turn a per-file callback back into the one event the user
                actually performed. Uploading inside each call instead is what
                made this a one-file uploader with a `multiple` flag that did
                nothing.
              */
              beforeUpload={(file, fileList) => {
                const batch = Array.isArray(fileList) && fileList.length ? fileList : [file];
                if (batch[0] !== file) return Upload.LIST_IGNORE;
                let rejected = 0;
                const allowed = batch.filter((f) =>
                  beforeUploadMaxSize(f, () => {
                    rejected += 1;
                  }),
                );
                if (rejected) {
                  message.error(t('max.10.mb') || 'File size must be 10 MB or less');
                }
                if (allowed.length) uploadMany(allowed);
                return Upload.LIST_IGNORE;
              }}
              style={{ background: '#f8fafc', borderColor: '#cbd5e1' }}
            >
              <p className='ant-upload-drag-icon'>
                <InboxOutlined style={{ color: '#0ea5e9' }} />
              </p>
              <p className='ant-upload-text'>{t('media.library.upload.hint')}</p>
              <p className='ant-upload-hint'>{t('max.10.mb')}</p>
              {uploading ? (
                <div style={{ marginTop: 8 }}>
                  <Spin />
                  <span style={{ marginLeft: 8 }}>
                    {t('uploading')}
                    {progress && progress.total > 1
                      ? ' ' + progress.done + '/' + progress.total
                      : ''}
                    …
                  </span>
                </div>
              ) : null}
            </Dragger>
          </div>
        </TabPane>
      </Tabs>

      {/* Lightbox for preview in manager mode */}
      <Modal
        visible={!!preview}
        onCancel={() => setPreview(null)}
        footer={null}
        title={preview?.title}
        width={720}
        centered
      >
        {preview ? (
          <div style={{ textAlign: 'center' }}>
            <img
              src={preview.url}
              alt={preview.title}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
            <div style={{ marginTop: 12, fontSize: 12, color: '#64748b', wordBreak: 'break-all' }}>
              {preview.url}
            </div>
          </div>
        ) : null}
      </Modal>

      <style>{`
        .ks-media { background: #fff; }
        .ks-media__body {
          display: grid;
          grid-template-columns: 200px 1fr;
          min-height: 480px;
          border-top: 1px solid #f1f5f9;
        }
        .ks-media__rail {
          border-right: 1px solid #f1f5f9;
          padding: 12px 8px;
          overflow-y: auto;
          max-height: 620px;
        }
        .ks-media__folder {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 8px 10px;
          border: none;
          background: transparent;
          border-radius: 6px;
          cursor: pointer;
          font-size: 13px;
          color: #334155;
          text-align: left;
          transition: background 120ms ease, color 120ms ease;
        }
        .ks-media__folder:hover { background: #f1f5f9; }
        .ks-media__folder.is-active {
          background: #e0f2fe;
          color: #075985;
          font-weight: 600;
        }
        .ks-media__folder-name {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ks-media__folder-count {
          font-size: 11px;
          color: #94a3b8;
          font-variant-numeric: tabular-nums;
        }
        .ks-media__folder.is-active .ks-media__folder-count { color: #0369a1; }
        .ks-media__folder--warn { color: #b45309; }
        .ks-media__folder--warn:hover { background: #fef3c7; }
        .ks-media__folder--warn.is-active { background: #fef3c7; color: #92400e; }
        .ks-media__rail-sep {
          height: 1px;
          background: #e2e8f0;
          margin: 8px 4px;
        }
        .ks-media__pane {
          display: flex;
          flex-direction: column;
          min-height: 480px;
        }
        .ks-media__toolbar {
          display: flex;
          gap: 8px;
          align-items: center;
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
        }
        .ks-media__notice {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: #fff7ed;
          color: #9a3412;
          font-size: 12px;
          border-bottom: 1px solid #fed7aa;
        }
        .ks-media__grid-wrap {
          flex: 1;
          overflow-y: auto;
          padding: 14px 16px;
          max-height: 560px;
        }
        .ks-media__grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 12px;
        }
        .ks-media__tile {
          position: relative;
          padding: 0;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          overflow: hidden;
          background: #fff;
          cursor: pointer;
          aspect-ratio: 1 / 1;
          transition: border-color 120ms ease, transform 120ms ease, box-shadow 120ms ease;
        }
        .ks-media__tile:hover {
          border-color: #0ea5e9;
          box-shadow: 0 4px 14px rgba(14,165,233,0.15);
          transform: translateY(-1px);
        }
        .ks-media__tile img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .ks-media__non-image {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748b;
          font-size: 11px;
          padding: 8px;
        }
        .ks-media__hover {
          position: absolute;
          inset: 0;
          background: rgba(15,23,42,0.45);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          opacity: 0;
          transition: opacity 120ms ease;
          font-size: 22px;
        }
        .ks-media__hover-label { font-size: 12px; font-weight: 600; }
        .ks-media__tile:hover .ks-media__hover { opacity: 1; }
        .ks-media__caption {
          position: absolute;
          left: 0;
          right: 0;
          bottom: 0;
          padding: 4px 8px;
          background: linear-gradient(to top, rgba(15,23,42,0.65), transparent);
          color: #fff;
          font-size: 10px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: left;
        }
        .ks-media__tile-delete {
          position: absolute;
          top: 6px;
          right: 6px;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(239, 68, 68, 0.9);
          color: #fff;
          border-radius: 50%;
          font-size: 12px;
          opacity: 0;
          transition: opacity 120ms ease;
          z-index: 2;
        }
        .ks-media__tile:hover .ks-media__tile-delete { opacity: 1; }
        .ks-media__tile.is-selected {
          outline: 2px solid #1677ff;
          outline-offset: -2px;
        }
        .ks-media__tile.is-selected .ks-media__hover { opacity: 1; }
        .ks-media__confirm {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 16px;
          border-top: 1px solid #f1f5f9;
          background: #fafcff;
        }
        .ks-media__confirm-count { font-size: 13px; color: #475569; }
        .ks-media__pager {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: flex-end;
          padding: 10px 16px;
          border-top: 1px solid #f1f5f9;
        }
        .ks-media__upload {
          padding: 24px;
        }
      `}</style>
    </div>
  );
}

/** Modal wrapper for the picker use-case. */
export default function MediaLibraryModal({
  open,
  onClose,
  onPick,
  /** Let the caller take several images in one visit — see MediaLibraryPanel. */
  multiple = false,
  defaultFolder = DEFAULT_FOLDER,
  title,
  accept,
  /** Caller-supplied advice shown on the upload tab — see MediaLibraryPanel. */
  hint,
}) {
  const { t } = useTranslation();
  return (
    <Modal
      title={title || t('media.library')}
      visible={open}
      onCancel={onClose}
      footer={null}
      width={960}
      destroyOnClose
      bodyStyle={{ padding: 0 }}
    >
      <MediaLibraryPanel
        mode='picker'
        multiple={multiple}
        onPick={(picked) => { onPick?.(picked); onClose?.(); }}
        defaultFolder={defaultFolder}
        accept={accept}
        hint={hint}
      />
    </Modal>
  );
}

function labelize(slug) {
  return String(slug).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
